import {
  DOCUMENT, DestroyRef, Injectable, Injector, PLATFORM_ID, Signal, computed, effect, inject, signal, untracked,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { isPlatformBrowser } from '@angular/common';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { AiAgentService } from '../API/ai-agent.service';
import { AlertService, UltimoErro } from '../alert.service';
import { UnifiedAuthService } from '../unified-auth.service';
import { GuiaOneService } from './guia-one.service';
import { TourRoteiroService } from './tour-roteiro.service';

/** D76 — as 4 fontes que a arbitração combina, nesta prioridade quando presentes. */
export type TipoTeaser = 'erro' | 'convite' | 'primeira-visita' | 'conclusao';

/** Forma comum de qualquer teaser do guia — `GuiaTeaserComponent` só sabe renderizar isto,
 *  nunca a fonte por trás (erro/convite/primeira-visita/conclusão). */
export interface TeaserGuia {
  tipo: TipoTeaser;
  icone: string;
  /** Uma linha (≤ 48 caracteres) — o balão é de linha única (C2/C3). */
  titulo: string;
  /** Único botão de aceite; ausente = só o X (conclusão). */
  acao?: { rotulo: string; executar: () => void };
  /** X: recusa/dispensa — conta/grava conforme a fonte. */
  fechar: () => void;
}

const COOLDOWN_ERRO_MS = 60_000;
const SILENCIO_MS = 30_000;
const ATRASO_PRIMEIRA_VISITA_MS = 1_500;

/**
 * D72/D73/D75/D76 — o guia "percebe": primeira visita a uma tela com trecho, erro explicado,
 * marco de conclusão, e o convite de tour (D70, migrado de `GuiaOneService.oferta`) — tudo
 * arbitrado num único `teaser` para o orbe nunca mostrar dois balões ao mesmo tempo nem competir
 * com o idle-help (`mostrarBalaoAjuda` no overlay exige `!teaser()`).
 *
 * `iniciar()` (chamado pelo overlay no `ngOnInit`, idempotente) liga a escuta de navegação e os
 * `effect`s de erro/conclusão — mesmo padrão de `AiIdleHelpService.iniciar()`: injetar o serviço
 * sozinho (ex. um teste que só lê `teaser()` do convite) não dispara nada sem chamar `iniciar()`.
 */
@Injectable({ providedIn: 'root' })
export class GuiaProatividadeService {
  private readonly router = inject(Router);
  private readonly guia = inject(GuiaOneService);
  private readonly motor = inject(TourRoteiroService);
  private readonly ai = inject(AiAgentService);
  private readonly alert = inject(AlertService);
  private readonly auth = inject(UnifiedAuthService);
  private readonly doc = inject(DOCUMENT);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly destroyRef = inject(DestroyRef);
  /** `effect()` fora do construtor (dentro de `iniciar()`) precisa de um injector explícito —
   *  ver o comentário de `iniciar()`. */
  private readonly injector = inject(Injector);

  private iniciado = false;

  // --- D70: convite (migrado de GuiaOneService/guia-teaser) — computed puro, sempre ativo,
  // não depende de `iniciar()` (é só leitura de outros signals, nenhum efeito colateral). ---
  private readonly _conviteTeaser: Signal<TeaserGuia | null> = computed<TeaserGuia | null>(() => {
    const modo = this.guia.oferta();
    if (!modo) return null;
    return {
      tipo: 'convite', icone: 'tour', titulo: this.guia.textoConvite(),
      acao: { rotulo: modo === 'iniciar' ? 'Começar' : 'Continuar', executar: () => void this.guia.aceitarOferta() },
      fechar: () => this.dispensar(() => this.guia.dispensarOferta()),
    };
  });

  // --- D73: erro ---
  private readonly _erroTeaser = signal<TeaserGuia | null>(null);
  /** -Infinity: o PRIMEIRO erro da sessão nunca é pego pelo cooldown, seja qual for a escala
   *  de `em` (produção usa `Date.now()`; testes podem usar números pequenos). */
  private ultimoErroTeaserEm = -Infinity;

  // --- D72: primeira visita ---
  private readonly _primeiraVisitaTeaser = signal<TeaserGuia | null>(null);
  private timeoutPrimeiraVisita: ReturnType<typeof setTimeout> | null = null;

  // --- D75b: conclusão (1x por sessão E 1x por empresa — `conclusaoVista` no blob) ---
  private readonly _conclusaoTeaser = signal<TeaserGuia | null>(null);
  /** null = ainda não observado (1ª leitura do effect não pode contar como borda — um usuário
   *  que JÁ tinha concluído a jornada em sessão anterior não pode ganhar o marco de novo só por
   *  o serviço acabar de nascer). */
  private jaConcluiuAntes: boolean | null = null;
  private conclusaoPendente = false;
  private conclusaoMostrada = false;

  // --- D76: silêncio total de 30s após qualquer dispensa, de qualquer fonte ---
  private readonly _silencioAte = signal(0);

  /** D76 — primeiro não-nulo, nesta ordem (erro > convite > primeira-visita > conclusão).
   *  `!ai.aberto()` é checado AQUI (não só em cada fonte) por construção: nenhum teaser passa
   *  disso por baixo do painel do assistente aberto, mesmo que uma fonte individual "esqueça"
   *  de checar — cinto e suspensório com o `[oculto]="mostrarBalao() || svc.aberto()"` do overlay. */
  readonly teaser: Signal<TeaserGuia | null> = computed(() => {
    if (this.ai.aberto() || this.motor.ativo() || !this.guia.rotaPermitida()) return null;
    if (Date.now() < this._silencioAte()) return null;
    return this._erroTeaser() ?? this._conviteTeaser() ?? this._primeiraVisitaTeaser() ?? this._conclusaoTeaser();
  });

  /** Liga a escuta de navegação (primeira visita) e os `effect`s de erro/conclusão. Chamado
   *  pelo `ngOnInit` do overlay — idempotente, e as partes client-only (timers/localStorage)
   *  são no-op no servidor (`aoNavegar`), mesmo padrão de `AiIdleHelpService.iniciar`. */
  iniciar(): void {
    if (this.iniciado) return;
    this.iniciado = true;

    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.aoNavegar());

    effect(() => this.aoErroMudar(), { injector: this.injector });
    effect(() => this.aoMotorMudar(), { injector: this.injector });
    effect(() => this.aoChatMudar(), { injector: this.injector });

    this.aoNavegar(); // a primeira tela da sessão (F5/deep-link) também conta como "navegação".
  }

  /** Review T5 (Important): abrir o chat CONSOME os teasers de erro/primeira-visita/conclusão —
   *  o usuário já foi falar com o assistente; sem isto o balão só ficava escondido enquanto o
   *  painel estava aberto e RESSURGIA no fechar, sem cooldown nenhum. O convite (D70) fica de
   *  fora de propósito: ele persiste até "Começar"/"Agora não". */
  private aoChatMudar(): void {
    if (!this.ai.aberto()) return;
    untracked(() => {
      if (this.timeoutPrimeiraVisita !== null) {
        clearTimeout(this.timeoutPrimeiraVisita);
        this.timeoutPrimeiraVisita = null;
      }
      this._erroTeaser.set(null);
      this._primeiraVisitaTeaser.set(null);
      this._conclusaoTeaser.set(null);
    });
  }

  // --- D72: primeira visita ---

  private aoNavegar(): void {
    if (this.timeoutPrimeiraVisita !== null) {
      clearTimeout(this.timeoutPrimeiraVisita);
      this.timeoutPrimeiraVisita = null;
    }
    this._primeiraVisitaTeaser.set(null); // o balão pendente, se havia, era da tela anterior.
    // #2577: fora de rota permitida (ex.: login após o logout) os balões de erro e de conclusão
    // morrem — `teaser` já os escondia, mas um erro antigo ressurgia no próximo login.
    if (!this.guia.rotaPermitida()) {
      this._erroTeaser.set(null);
      this._conclusaoTeaser.set(null);
    }
    if (!isPlatformBrowser(this.platformId)) return; // setTimeout/localStorage: só no browser.
    const trecho = this.guia.trechoAtual();
    if (!trecho || this.motor.ativo() || this.ai.aberto() || !this.guia.rotaPermitida()) return;
    const tela = String(this.guia.subPerfilAtual());
    if (this.lerTelasVistas().includes(tela)) return;
    this.timeoutPrimeiraVisita = setTimeout(() => this.mostrarPrimeiraVisita(tela), ATRASO_PRIMEIRA_VISITA_MS);
  }

  private mostrarPrimeiraVisita(tela: string): void {
    this.timeoutPrimeiraVisita = null;
    // O blob pode ter hidratado durante o atraso de 1,5 s — F5 em tela já dispensada.
    if (this.lerTelasVistas().includes(tela)) return;
    this._primeiraVisitaTeaser.set({
      tipo: 'primeira-visita', icone: 'tour',
      titulo: `Primeira vez em ${this.guia.nomeTelaAtual()}? Te mostro.`,
      acao: { rotulo: 'Mostrar', executar: () => this.resolverPrimeiraVisita(tela, () => void this.guia.executar('tour-tela')) },
      fechar: () => this.dispensar(() => this.resolverPrimeiraVisita(tela)),
    });
  }

  /** Aceitar OU dispensar gravam a visita — a dica nunca repete nesta tela (D72), mesmo que o
   *  usuário tenha recusado (repetir uma dica recusada é ruído, não ajuda). */
  private resolverPrimeiraVisita(tela: string, aoAceitar?: () => void): void {
    this.gravarTelaVista(tela);
    this._primeiraVisitaTeaser.set(null);
    aoAceitar?.();
  }

  // --- D73: erro ---

  private aoErroMudar(): void {
    const erro = this.alert.ultimoErro();
    if (!erro) return;
    // `untracked`: só `alert.ultimoErro()` deve reagendar este effect — ler aberto()/
    // rotaPermitida() reativamente reabriria (de forma surpreendente) o teaser de um erro ANTIGO
    // quando o chat fechasse bem depois, sem erro novo nenhum ter acontecido.
    const chatAberto = untracked(() => this.ai.aberto());
    const rotaOk = untracked(() => this.guia.rotaPermitida());
    if (chatAberto || !rotaOk) return;
    if (erro.em - this.ultimoErroTeaserEm < COOLDOWN_ERRO_MS) return;
    this.ultimoErroTeaserEm = erro.em;
    this._erroTeaser.set({
      tipo: 'erro', icone: 'error_outline',
      titulo: 'Deu erro aqui. Explico?',
      acao: { rotulo: 'Explicar', executar: () => this.explicarErro(erro) },
      fechar: () => this.dispensar(() => this._erroTeaser.set(null)),
    });
  }

  private explicarErro(erro: UltimoErro): void {
    this._erroTeaser.set(null); // não sobra pendurado quando o chat fechar de novo.
    this.ai.abrir();
    const tela = this.guia.nomeTelaAtual();
    void this.ai.converseTexto(
      `Apareceu este erro na tela ${tela}: "${erro.titulo}: ${erro.mensagem}". O que significa e como resolvo?`);
  }

  // --- D75b: conclusão ---

  private aoMotorMudar(): void {
    const concluiu = this.motor.jaConcluiu();
    const ativo = this.motor.ativo();
    const anterior = this.jaConcluiuAntes;
    this.jaConcluiuAntes = concluiu;
    if (anterior === false && concluiu) this.conclusaoPendente = true;
    if (!this.conclusaoPendente || ativo || this.conclusaoMostrada) return;
    // B5: o marco é 1x por EMPRESA (blob), não só por sessão — quem já o viu noutra máquina não
    // ganha de novo. `untracked`: a memória não deve reagendar este effect (só o motor deve).
    if (untracked(() => this.guia.guiaDaEmpresa().conclusaoVista)) {
      this.conclusaoPendente = false;
      return;
    }
    this.conclusaoPendente = false;
    this.conclusaoMostrada = true;
    untracked(() => this.guia.atualizarGuiaDaEmpresa((e) => ({ ...e, conclusaoVista: true })));
    this._conclusaoTeaser.set({
      tipo: 'conclusao', icone: 'celebration',
      titulo: 'Tour concluído! Estou aqui se precisar.',
      fechar: () => this.dispensar(() => this._conclusaoTeaser.set(null)),
    });
  }

  // --- D76: silêncio compartilhado ---

  /** Qualquer dispensa (o X, de QUALQUER fonte) aplica 30s de silêncio total — nunca dois
   *  balões em sequência imediata. */
  private dispensar(efeito: () => void): void {
    efeito();
    this._silencioAte.set(Date.now() + SILENCIO_MS);
    setTimeout(() => this._silencioAte.set(0), SILENCIO_MS);
  }

  // --- telasVistas (D72): blob por empresa + leitura do localStorage legado ---

  /** B4: blob por empresa é a fonte; o `localStorage` legado só é LIDO (quem já dispensou nesta
   *  máquina não vê a dica de novo) — escrita só no blob. */
  private lerTelasVistas(): string[] {
    return [...new Set([...this.guia.guiaDaEmpresa().telasDispensadas, ...this.lerTelasVistasLegado()])];
  }

  private chaveTelasVistasLegado(): string {
    const usuario = this.auth.getUserInfos() as { idUsuario?: number } | null;
    return `guia-one:telas-vistas:${usuario?.idUsuario ?? 'anon'}`;
  }

  private lerTelasVistasLegado(): string[] {
    try {
      const raw = this.doc.defaultView?.localStorage.getItem(this.chaveTelasVistasLegado());
      if (!raw) return [];
      const parsed: unknown = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === 'string') : [];
    } catch {
      return []; // modo privado/quota estourada: a dica só repete — nunca pode quebrar a UI.
    }
  }

  private gravarTelaVista(tela: string): void {
    this.guia.atualizarGuiaDaEmpresa((e) =>
      e.telasDispensadas.includes(tela) ? e : { ...e, telasDispensadas: [...e.telasDispensadas, tela] });
  }
}
